import { createSignal, onMount, For, Show, Switch, Match, createMemo } from "solid-js";
import { api, type Organization, type OrganizationMember, type OrganizationAuthProvider, type User, type SessionInfo, type OrganizationInvite, type Project, type EmailVerificationLinkInfo } from "../shared";
import { getAdminRuntimeConfig, managedOrgLoginUrl, managedProjectUrl, managedSearcherUrl } from "../runtime";
import { RoleBadge } from "./RoleBadge";
import { StatusBadge } from "./StatusBadge";
import { AuthSessionsTable } from "./AuthSessionsTable";
import { RefreshTokensTable } from "./RefreshTokensTable";
import { CopyButton } from "./CopyButton";
import { LinkPreview } from "./LinkPreview";
import { UsersTab, type EditData } from "./users/UsersTab";
import { Pagination } from "./Pagination";
import { DropdownMenu } from "./DropdownMenu";
import { VerificationLinksSection } from "./VerificationLinksSection";
import { OrganizationCacheTab } from "./OrganizationCacheTab";

const PROVIDER_BASE_KEYS: Record<string, { key: string; label: string; type: string }[]> = {
  email: [
    { key: "requireEmailVerification", label: "Require Email Verification", type: "bool" },
    { key: "passwordMinLength", label: "Min Password Length", type: "number" },
  ],
  google: [
    { key: "clientId", label: "Client ID", type: "text" },
    { key: "clientSecret", label: "Client Secret", type: "text" },
    { key: "redirectUri", label: "Redirect URI", type: "text" },
  ],
  anonymous: [],
};

export function OrganizationsPanel() {
  const ORG_PREFIX_PATTERN = /^[a-z0-9]+$/;
  const PROJECT_SEGMENT_PATTERN = /^[a-z0-9]+$/;
  const normalizeSegment = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
  const projectCacheStatus = (project: Project) => {
    const generatedAt = project.blocksCacheGeneratedAt ? Date.parse(project.blocksCacheGeneratedAt) : NaN;
    const expiresAt = project.blocksCacheExpiresAt ? Date.parse(project.blocksCacheExpiresAt) : NaN;
    const invalidatedAt = project.blocksCacheInvalidatedAt ? Date.parse(project.blocksCacheInvalidatedAt) : NaN;
    if (!project.blocksCacheGeneratedAt) return "missing";
    if (project.blocksCacheInvalidatedAt && (!Number.isFinite(generatedAt) || invalidatedAt >= generatedAt)) return "invalidated";
    if (project.blocksCacheExpiresAt && Number.isFinite(expiresAt) && expiresAt <= Date.now()) return "stale";
    return "fresh";
  };

  const [orgs, setOrgs] = createSignal<Organization[]>([]);
  const [users, setUsers] = createSignal<User[]>([]);
  const [error, setError] = createSignal("");
  const [message, setMessage] = createSignal("");
  const [loadingTab, setLoadingTab] = createSignal<string | null>(null);
  const [loadedTabs, setLoadedTabs] = createSignal<Set<string>>(new Set());

  const [selectedOrgId, setSelectedOrgId] = createSignal<string | null>(null);
  const [orgSubTab, setOrgSubTab] = createSignal<"members" | "auth" | "users" | "links" | "invites" | "projects" | "cache">("members");

  // Detail data
  const [members, setMembers] = createSignal<OrganizationMember[]>([]);
  const [providers, setProviders] = createSignal<OrganizationAuthProvider[]>([]);
  const [orgUsers, setOrgUsers] = createSignal<any[]>([]);
  const [links, setLinks] = createSignal<SessionInfo[]>([]);
  const [invites, setInvites] = createSignal<OrganizationInvite[]>([]);
  const [projects, setProjects] = createSignal<Project[]>([]);
  const [showProviders, setShowProviders] = createSignal(false);
  const [authLinksPage, setAuthLinksPage] = createSignal(1);
  const AUTH_LINKS_PER_PAGE = 20;

  // Modals
  const [showCreate, setShowCreate] = createSignal(false);
  const [newOrgName, setNewOrgName] = createSignal("");
  const [newOrgSlug, setNewOrgSlug] = createSignal("");
  const [newOrgOwner, setNewOrgOwner] = createSignal("");
  const [showEditOrg, setShowEditOrg] = createSignal(false);
  const [editOrgName, setEditOrgName] = createSignal("");
  const [editOrgSlug, setEditOrgSlug] = createSignal("");
  const [createOrgWithUser, setCreateOrgWithUser] = createSignal(false);
  const [createOrgUserEmail, setCreateOrgUserEmail] = createSignal("");
  const [createOrgUserDisplayName, setCreateOrgUserDisplayName] = createSignal("");
  const [createOrgUserPassword, setCreateOrgUserPassword] = createSignal("");
  const [showAddMember, setShowAddMember] = createSignal(false);
  const [addMemberUserId, setAddMemberUserId] = createSignal("");
  const [addMemberRole, setAddMemberRole] = createSignal("manager");
  const [showInvite, setShowInvite] = createSignal(false);
  const [inviteEmail, setInviteEmail] = createSignal("");
  const [inviteRole, setInviteRole] = createSignal("manager");
  const [showLinkResult, setShowLinkResult] = createSignal<{ email: string; link: string } | null>(null);
  const [showLinkGen, setShowLinkGen] = createSignal(false);
  const [linkGenUserId, setLinkGenUserId] = createSignal("");
  const [linkGenPresetEmail, setLinkGenPresetEmail] = createSignal<string | null>(null);
  const [linkGenResult, setLinkGenResult] = createSignal<{ link: string } | null>(null);
  const [linkGenLockedUser, setLinkGenLockedUser] = createSignal(false);
  const [linkGenExpiry, setLinkGenExpiry] = createSignal(300);
  const [verifyLinkResult, setVerifyLinkResult] = createSignal<{ email: string; link: string; expiresAt: string } | null>(null);
  const [showVerifyLinkGen, setShowVerifyLinkGen] = createSignal(false);
  const [verifyLinkGenUserId, setVerifyLinkGenUserId] = createSignal("");
  const [verifyLinkGenEmail, setVerifyLinkGenEmail] = createSignal("");
  const [verifyLinkGenExpiry, setVerifyLinkGenExpiry] = createSignal(48);
  const [showSetPw, setShowSetPw] = createSignal(false);
  const [setPwUserId, setSetPwUserId] = createSignal("");
  const [setPwEmail, setSetPwEmail] = createSignal("");
  const [setPwPassword, setSetPwPassword] = createSignal("");
  const [userRoleFilter, setUserRoleFilter] = createSignal("");
  const [authFilter, setAuthFilter] = createSignal<"" | "authenticated" | "anonymous">("");
  const [showAddUser, setShowAddUser] = createSignal(false);
  const [addUserEmail, setAddUserEmail] = createSignal("");
  const [addUserDisplayName, setAddUserDisplayName] = createSignal("");
  const [addUserPassword, setAddUserPassword] = createSignal("");
  const [addUserRole, setAddUserRole] = createSignal("manager");
  const [showCreateProject, setShowCreateProject] = createSignal(false);
  const [cpName, setCpName] = createSignal("");
  const [cpDescription, setCpDescription] = createSignal("");
  const [cpSlug, setCpSlug] = createSignal("");
  const [cpVisibility, setCpVisibility] = createSignal("private");
  const [cpBlockSize, setCpBlockSize] = createSignal(50);
  const [cpAccessMode, setCpAccessMode] = createSignal("open");
  const [cpOwnerId, setCpOwnerId] = createSignal("");
  const [cpCreating, setCpCreating] = createSignal(false);
  const [showEditProject, setShowEditProject] = createSignal(false);
  const [editProjectId, setEditProjectId] = createSignal("");
  const [epName, setEpName] = createSignal("");
  const [epDescription, setEpDescription] = createSignal("");
  const [epSlug, setEpSlug] = createSignal("");
  const [epLifecycle, setEpLifecycle] = createSignal("");
  const [epVisibility, setEpVisibility] = createSignal("");
  const [epAccessMode, setEpAccessMode] = createSignal("");
  const [epBlockSize, setEpBlockSize] = createSignal(50);
  const [epSaving, setEpSaving] = createSignal(false);
  const [expandedProject, setExpandedProject] = createSignal<string | null>(null);
  const [projectMembers, setProjectMembers] = createSignal<Record<string, import("../shared").ProjectMember[]>>({});
  const [loadingProjectMembers, setLoadingProjectMembers] = createSignal(false);
  const [showAddProjectMember, setShowAddProjectMember] = createSignal(false);
  const [addProjectMemberUserId, setAddProjectMemberUserId] = createSignal("");
  const [addProjectMemberRole, setAddProjectMemberRole] = createSignal("manager");
  const [addProjectMemberProjectId, setAddProjectMemberProjectId] = createSignal("");
  const [membersPage, setMembersPage] = createSignal(1);
  const [membersPerPage, setMembersPerPage] = createSignal(20);
  const [membersTotal, setMembersTotal] = createSignal(0);
  const [usersPage, setUsersPage] = createSignal(1);
  const [usersPerPage, setUsersPerPage] = createSignal(20);
  const [usersTotal, setUsersTotal] = createSignal(0);
  const [linksPage, setLinksPage] = createSignal(1);
  const [linksPerPage, setLinksPerPage] = createSignal(20);
  const [linksTotal, setLinksTotal] = createSignal(0);
  const [invitesPage, setInvitesPage] = createSignal(1);
  const [invitesPerPage, setInvitesPerPage] = createSignal(20);
  const [invitesTotal, setInvitesTotal] = createSignal(0);

  async function loadOrgs() {
    try {
      setOrgs(await api.organizations.list());
      setUsers(await api.users.list());
    } catch (e: any) { setError(e.message); }
  }

  onMount(loadOrgs);

  const [loading, setLoading] = createSignal(false);

  // Hash watch for /#organizations/<id>/<tab>
  function hashOrgId(): { id: string; tab?: string } | null {
    const h = location.hash.replace(/^#/, "");
    const m = h.match(/^organizations\/([^/]+)(?:\/(.+))?$/);
    if (!m) return null;
    return { id: m[1], tab: m[2] };
  }

  const VALID_TABS = ["members", "auth", "users", "links", "invites", "projects", "cache"] as const;

  function selectOrg(id: string) {
    setSelectedOrgId(id);
    setLoadedTabs(new Set());
    setMembersPage(1); setUsersPage(1); setLinksPage(1); setInvitesPage(1);
    const ht = hashOrgId();
    const tab = (ht?.tab && (VALID_TABS as readonly string[]).includes(ht.tab)) ? ht.tab : "members";
    location.hash = `organizations/${id}/${tab}`;
    setOrgSubTab(tab as any);
    loadOrgTab(id, tab);
  }

  function goBack() {
    setSelectedOrgId(null);
    location.hash = "organizations";
  }

  async function loadOrgTab(id: string, tab: string) {
    setLoadingTab(tab);
    try {
      switch (tab) {
        case "members": {
          const r = await api.organizations.members(id, { limit: membersPerPage(), offset: (membersPage() - 1) * membersPerPage() });
          setMembers(r.items);
          setMembersTotal(r.total);
          break;
        }
        case "auth":
          setProviders(await api.organizations.authProviders(id));
          {
            const s = await api.sessions.list({ organizationId: id, limit: 200 });
            setLinks(s.items || []);
            setLinksTotal(s.total);
          }
          break;
        case "users": {
          const r = await api.organizations.orgUsers(id, undefined, { limit: usersPerPage(), offset: (usersPage() - 1) * usersPerPage() }) as any;
          setOrgUsers(r.items);
          setUsersTotal(r.total);
          break;
        }
        case "links": {
          const s = await api.sessions.list({ organizationId: id, limit: linksPerPage(), offset: (linksPage() - 1) * linksPerPage() });
          setLinks(s.items);
          setLinksTotal(s.total);
          break;
        }
        case "invites": {
          const r = await api.organizations.invites(id, { limit: invitesPerPage(), offset: (invitesPage() - 1) * invitesPerPage() });
          setInvites(r.items);
          setInvitesTotal(r.total);
          break;
        }
        case "projects":
        case "cache":
          setProjects(await api.organizations.projects(id));
          break;
      }
      setLoadedTabs((prev) => new Set(prev).add(tab));
    } catch (e: any) { setError(e.message); }
    finally {
      setLoadingTab((current) => (current === tab ? null : current));
    }
  }

  function membersPageChange(page: number) { setMembersPage(page); loadOrgTab(selectedOrgId()!, "members"); }
  function membersPerPageChange(pp: number) { setMembersPage(1); setMembersPerPage(pp); loadOrgTab(selectedOrgId()!, "members"); }
  function usersPageChange(page: number) { setUsersPage(page); loadOrgTab(selectedOrgId()!, "users"); }
  function usersPerPageChange(pp: number) { setUsersPage(1); setUsersPerPage(pp); loadOrgTab(selectedOrgId()!, "users"); }
  function linksPageChange(page: number) { setLinksPage(page); loadOrgTab(selectedOrgId()!, "links"); }
  function linksPerPageChange(pp: number) { setLinksPage(1); setLinksPerPage(pp); loadOrgTab(selectedOrgId()!, "links"); }
  function invitesPageChange(page: number) { setInvitesPage(page); loadOrgTab(selectedOrgId()!, "invites"); }
  function invitesPerPageChange(pp: number) { setInvitesPage(1); setInvitesPerPage(pp); loadOrgTab(selectedOrgId()!, "invites"); }

  function switchOrgTab(tab: "members" | "auth" | "users" | "links" | "invites" | "projects" | "cache") {
    setOrgSubTab(tab);
    const id = selectedOrgId();
    if (id) location.hash = `organizations/${id}/${tab}`;
    if (!loadedTabs().has(tab) && id) {
      loadOrgTab(id, tab);
    }
  }

  onMount(() => {
    const ht = hashOrgId();
    if (ht) {
      setSelectedOrgId(ht.id);
      const tab = ht.tab && (VALID_TABS as readonly string[]).includes(ht.tab) ? ht.tab : "members";
      setOrgSubTab(tab as any);
      loadOrgTab(ht.id, tab);
    }
    const onHash = () => {
      const newHt = hashOrgId();
      if (!newHt && selectedOrgId()) {
        setSelectedOrgId(null);
      } else if (newHt && newHt.id !== selectedOrgId()) {
        setSelectedOrgId(newHt.id);
        const tab = newHt.tab && (VALID_TABS as readonly string[]).includes(newHt.tab) ? newHt.tab : "members";
        setOrgSubTab(tab as any);
        setLoadedTabs(new Set());
        loadOrgTab(newHt.id, tab);
      } else if (newHt && newHt.tab && newHt.tab !== orgSubTab()) {
        switchOrgTab(newHt.tab as any);
      }
    };
    window.addEventListener("hashchange", onHash);
  });

  async function doCreateOrg() {
    if (!newOrgName()) return;
    const orgSlug = normalizeSegment(newOrgSlug());
    if (!ORG_PREFIX_PATTERN.test(orgSlug)) {
      setError("Organization prefix slug must use only lowercase letters and numbers.");
      return;
    }
    try {
      let ownerId = newOrgOwner();
      if (createOrgWithUser()) {
        if (!createOrgUserEmail() || !createOrgUserDisplayName() || !createOrgUserPassword()) {
          setError("Email, display name, and password are required for new owner");
          return;
        }
        const userResult = await api.users.create({
          email: createOrgUserEmail(),
          displayName: createOrgUserDisplayName(),
          password: createOrgUserPassword(),
        });
        ownerId = userResult.id;
      }
      if (!ownerId) return;
      await api.organizations.create({ name: newOrgName(), ownerUserId: ownerId, slug: orgSlug });
      setShowCreate(false);
      setNewOrgName(""); setNewOrgSlug(""); setNewOrgOwner("");
      setCreateOrgWithUser(false);
      setCreateOrgUserEmail(""); setCreateOrgUserDisplayName(""); setCreateOrgUserPassword("");
      loadOrgs();
    } catch (e: any) { setError(e.message); }
  }

  function startEditOrg() {
    const org = selOrg();
    if (!org) return;
    setEditOrgName(org.name);
    setEditOrgSlug(org.slug);
    setShowEditOrg(true);
  }

  async function doEditOrg() {
    const org = selOrg();
    if (!org) return;
    const orgSlug = normalizeSegment(editOrgSlug());
    if (!ORG_PREFIX_PATTERN.test(orgSlug)) {
      setError("Organization prefix slug must use only lowercase letters and numbers.");
      return;
    }
    try {
      await api.organizations.update(org.id, {
        name: editOrgName() || undefined,
        slug: orgSlug,
      });
      setShowEditOrg(false);
      await loadOrgs();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function invalidateOrganizationProjectBlockCaches() {
    if (!selectedOrgId()) return;
    try {
      await api.organizations.invalidateProjectBlockCaches(selectedOrgId()!);
      await loadOrgTab(selectedOrgId()!, orgSubTab() === "cache" ? "cache" : "projects");
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function invalidateProjectBlockCache(projectId: string) {
    try {
      await api.projects.invalidateBlockCache(projectId);
      if (selectedOrgId()) {
        await loadOrgTab(selectedOrgId()!, orgSubTab() === "cache" ? "cache" : "projects");
      }
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function loadOrgUsers(id: string) {
    try {
      const role = userRoleFilter();
      const r = await api.organizations.orgUsers(id, role || undefined) as any;
      setOrgUsers(r.items as any);
      setUsersTotal(r.total);
    } catch (e: any) { setError(e.message); }
  }

  async function loadUsersAndMembers(id: string) {
    try {
      const [m, u] = await Promise.all([
        api.organizations.members(id),
        api.organizations.orgUsers(id),
      ]);
      setMembers(m.items);
      setMembersTotal(m.total);
      setOrgUsers(u.items as any);
      setUsersTotal(u.total);
    } catch (e: any) { setError(e.message); }
  }

  async function doAddUser() {
    if (!addUserEmail() || !selectedOrgId()) return;
    try {
      let userId: string | null = null;
      try {
        const result = await api.users.create({
          email: addUserEmail(),
          displayName: addUserDisplayName() || addUserEmail(),
          password: addUserPassword() || undefined,
        });
        userId = result.id;
      } catch (e: any) {
        if (!String(e?.message ?? "").includes("Email already in use")) {
          throw e;
        }
        const allUsers = await api.users.list();
        setUsers(allUsers);
        userId = allUsers.find((user) => user.email.toLowerCase() === addUserEmail().toLowerCase())?.id ?? null;
        if (!userId) {
          throw new Error("User already exists, but could not be loaded for organization membership.");
        }
      }
      await api.organizations.addMember(selectedOrgId()!, userId, addUserRole());
      setShowAddUser(false);
      setAddUserEmail(""); setAddUserDisplayName(""); setAddUserPassword(""); setAddUserRole("manager");
      loadUsersAndMembers(selectedOrgId()!);
    } catch (e: any) { setError(e.message); }
  }

  async function doUpdateMember(userId: string, fields: { role?: string; status?: string }) {
    try { await api.organizations.updateMember(selectedOrgId()!, userId, fields); loadUsersAndMembers(selectedOrgId()!); } catch (e: any) { setError(e.message); }
  }

  async function doRemoveMember(userId: string) {
    try { await api.organizations.removeMember(selectedOrgId()!, userId); loadUsersAndMembers(selectedOrgId()!); } catch (e: any) { setError(e.message); }
  }

  async function doAddMember() {
    if (!addMemberUserId() || !selectedOrgId()) return;
    try {
      await api.organizations.addMember(selectedOrgId()!, addMemberUserId(), addMemberRole());
      setShowAddMember(false); setAddMemberRole("manager"); setAddMemberUserId("");
      if (selectedOrgId()) loadUsersAndMembers(selectedOrgId()!);
    } catch (e: any) { setError(e.message); }
  }

  async function doToggleProvider(providerId: string, enabled: number) {
    try {
      await api.organizations.updateAuthProvider(selectedOrgId()!, providerId, { enabled });
      loadOrgTab(selectedOrgId()!, "auth");
    } catch (e: any) { setError(e.message); }
  }

  async function doGenLoginLink(userId: string) {
    try {
      const result = await api.users.loginLink(userId, selectedOrgId() ?? undefined, linkGenExpiry());
      setLinkGenResult({ link: result.link });
      loadOrgTab(selectedOrgId()!, "links");
    } catch (e: any) { setError(e.message); }
  }

  async function doGenVerifyLink(userId: string, email: string) {
    try {
      const result = await api.users.emailVerificationLink(userId, verifyLinkGenExpiry());
      setVerifyLinkResult({ email, link: result.link, expiresAt: result.expiresAt });
      setVerifyLinkGenUserId(userId);
      setVerifyLinkGenEmail(email);
      setShowVerifyLinkGen(true);
    } catch (e: any) { setError(e.message); }
  }

  async function doVerifyEmailDirect(userId: string) {
    try {
      await api.users.verifyEmail(userId);
      loadOrgTab(selectedOrgId()!, "users");
    } catch (e: any) { setError(e.message); }
  }

  async function doUnverifyEmail(userId: string) {
    try {
      await api.users.unverifyEmail(userId);
      loadOrgTab(selectedOrgId()!, "users");
    } catch (e: any) { setError(e.message); }
  }

  function closeVerifyLinkGenModal() {
    setShowVerifyLinkGen(false);
    setVerifyLinkGenUserId("");
    setVerifyLinkGenEmail("");
    setVerifyLinkResult(null);
  }

  function closeLinkGenModal() {
    setShowLinkGen(false);
    setLinkGenUserId("");
    setLinkGenPresetEmail(null);
    setLinkGenResult(null);
    setLinkGenLockedUser(false);
  }

  async function doRevokeLink(id: string) {
    try { await api.sessions.revoke(id); loadOrgTab(selectedOrgId()!, "links"); } catch (e: any) { setError(e.message); }
  }

  async function revokeLoginLink(id: string) {
    try { await api.sessions.revoke(id); loadOrgTab(selectedOrgId()!, "auth"); } catch (e: any) { setError(e.message); }
  }

  async function doCreateInvite() {
    if (!inviteEmail() || !selectedOrgId()) return;
    const owner = members().find((m) => m.role === "owner");
    try {
      const result = await api.organizations.createInvite(selectedOrgId()!, { email: inviteEmail(), role: inviteRole(), createdBy: owner?.userId ?? "" });
      setShowInvite(false); setInviteEmail("");
      setShowLinkResult({ email: inviteEmail(), link: result.link });
      loadOrgTab(selectedOrgId()!, "invites");
    } catch (e: any) { setError(e.message); }
  }

  async function doRemoveInvite(inviteId: string) {
    try { await api.organizations.removeInvite(selectedOrgId()!, inviteId); loadOrgTab(selectedOrgId()!, "invites"); } catch (e: any) { setError(e.message); }
  }

  async function handleSaveEdit(data: EditData) {
    try {
      if (data.newRole !== data.originalRole) {
        await api.organizations.updateMember(selectedOrgId()!, data.userId, { role: data.newRole || undefined });
      }
      if (data.newMemberStatus !== (data.originalMemberStatus ?? "approved")) {
        await api.organizations.updateMember(selectedOrgId()!, data.userId, { status: data.newMemberStatus });
      }
      if (data.newUserStatus !== data.originalUser.status) {
        await api.users.update(data.userId, { status: data.newUserStatus });
      }
      await loadUsersAndMembers(selectedOrgId()!);
    } catch (e: any) { setError(e.message); }
  }

  async function doCreateProjectInOrg() {
    if (!cpName() || !selectedOrgId()) return;
    const projectSlug = normalizeSegment(cpSlug());
    if (!PROJECT_SEGMENT_PATTERN.test(projectSlug)) {
      setError("Project slug must use only lowercase letters and numbers.");
      return;
    }
    setCpCreating(true);
    try {
      await api.projects.create({
        name: cpName(),
        description: cpDescription(),
        slug: projectSlug,
        visibility: cpVisibility(),
        blockSize: cpBlockSize(),
        accessMode: cpAccessMode(),
        ownerId: cpOwnerId() || undefined,
        organizationId: selectedOrgId()!,
      });
      setShowCreateProject(false);
      setCpName(""); setCpDescription(""); setCpVisibility("private");
      setCpSlug("");
      setCpBlockSize(50); setCpAccessMode("open"); setCpOwnerId("");
      loadOrgTab(selectedOrgId()!, "projects");
    } catch (e: any) { setError(e.message); }
    setCpCreating(false);
  }

  async function toggleProjectMembers(id: string) {
    if (expandedProject() === id) { setExpandedProject(null); return; }
    setExpandedProject(id);
    if (!projectMembers()[id]) {
      setLoadingProjectMembers(true);
      try {
        const m = await api.projects.members(id);
        setProjectMembers((p) => ({ ...p, [id]: m }));
      } catch (e: any) { setError(e.message); }
      setLoadingProjectMembers(false);
    }
  }

  async function addProjectMember() {
    const projectId = addProjectMemberProjectId();
    const userId = addProjectMemberUserId();
    if (!projectId || !userId) return;
    try {
      await api.projects.addMember(projectId, userId, addProjectMemberRole());
      setShowAddProjectMember(false);
      setAddProjectMemberUserId("");
      setAddProjectMemberRole("manager");
      const m = await api.projects.members(projectId);
      setProjectMembers((p) => ({ ...p, [projectId]: m }));
    } catch (e: any) { setError(e.message); }
  }

  async function removeProjectMember(projectId: string, userId: string) {
    try {
      await api.projects.removeMember(projectId, userId);
      const m = await api.projects.members(projectId);
      setProjectMembers((p) => ({ ...p, [projectId]: m }));
    } catch (e: any) { setError(e.message); }
  }

  async function updateProjectMemberRole(projectId: string, userId: string, role: string) {
    try {
      await api.projects.updateMember(projectId, userId, { role });
      const m = await api.projects.members(projectId);
      setProjectMembers((p) => ({ ...p, [projectId]: m }));
    } catch (e: any) { setError(e.message); }
  }

  function startEditProject(p: import("../shared").Project) {
    setEditProjectId(p.id);
    setEpName(p.name);
    setEpDescription(p.description ?? "");
    setEpSlug(p.slug ?? "");
    setEpLifecycle(p.lifecycle);
    setEpVisibility(p.visibility);
    setEpAccessMode((p as any).accessMode ?? "open");
    setEpBlockSize(p.blockSize);
    setShowEditProject(true);
  }

  async function doEditProject() {
    if (!editProjectId()) return;
    const projectSlug = normalizeSegment(epSlug());
    if (!PROJECT_SEGMENT_PATTERN.test(projectSlug)) {
      setError("Project slug must use only lowercase letters and numbers.");
      return;
    }
    setEpSaving(true);
    try {
      await api.projects.update(editProjectId(), {
        name: epName() || undefined,
        description: epDescription() || undefined,
        slug: projectSlug,
        lifecycle: epLifecycle() || undefined,
        visibility: epVisibility() || undefined,
        accessMode: epAccessMode() || undefined,
        blockSize: epBlockSize(),
      });
      setShowEditProject(false);
      loadOrgTab(selectedOrgId()!, "projects");
    } catch (e: any) { setError(e.message); }
    setEpSaving(false);
  }

  const selOrg = () => orgs().find((o) => o.id === selectedOrgId());

  const filteredUsers = createMemo(() => {
    let list = orgUsers() || [];
    if (authFilter() === "authenticated") list = list.filter((u: any) => !u.isAnonymous);
    if (authFilter() === "anonymous") list = list.filter((u: any) => u.isAnonymous);
    return list;
  });

  const createProjectFullSlug = () => {
    const prefix = selOrg()?.slug ?? "org";
    const segment = normalizeSegment(cpSlug()) || "project";
    return `${prefix}-${segment}`;
  };
  const editProjectFullSlug = () => {
    const prefix = selOrg()?.slug ?? "org";
    const segment = normalizeSegment(epSlug()) || "project";
    return `${prefix}-${segment}`;
  };

  return (
    <div class="space-y-6">
      <Show when={error()}><div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm flex items-center justify-between"><span>{error()}</span><button onClick={() => setError("")} class="text-gray-500 hover:text-gray-300 ml-4 cursor-pointer">✕</button></div></Show>
      <Show when={message()}><div class="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg text-sm">{message()}</div></Show>

      {/* Link result modal */}
      <Show when={showLinkResult()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center">
          <div class="absolute inset-0 bg-black/60" onClick={() => setShowLinkResult(null)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-1">Link Generated</h3>
            <p class="text-sm text-gray-400 mb-3">{showLinkResult()!.email}</p>
            <div class="mb-3">
              <LinkPreview href={showLinkResult()!.link} />
            </div>
            <div class="flex justify-end gap-3">
              <CopyButton link={showLinkResult()!.link} compact />
              <button onClick={() => setShowLinkResult(null)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Close</button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={verifyLinkResult()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center">
          <div class="absolute inset-0 bg-black/60" onClick={() => setVerifyLinkResult(null)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-1">Email Verification Link</h3>
            <p class="text-sm text-gray-400 mb-3">{verifyLinkResult()!.email} - expires {new Date(verifyLinkResult()!.expiresAt).toLocaleString()}</p>
            <div class="mb-3">
              <LinkPreview href={verifyLinkResult()!.link} />
            </div>
            <div class="flex justify-end gap-3">
              <CopyButton link={verifyLinkResult()!.link} compact />
              <button onClick={() => setVerifyLinkResult(null)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Close</button>
            </div>
          </div>
        </div>
      </Show>

      <Switch>
        {/* ORG LIST */}
        <Match when={!selectedOrgId()}>
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold">Organizations ({orgs().length})</h2>
            <button onClick={() => setShowCreate(true)} class="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-500 transition-colors cursor-pointer">+ Create Organization</button>
          </div>
          <div class="border border-gray-800 rounded-lg overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-900 text-gray-400"><tr><th class="text-left px-4 py-3 font-medium">Name</th><th class="text-left px-4 py-3 font-medium">Prefix Slug</th><th class="text-left px-4 py-3 font-medium">Owner</th><th class="text-center px-4 py-3 font-medium">Members</th><th class="text-left px-4 py-3 font-medium">Created</th></tr></thead>
              <tbody class="divide-y divide-gray-800">
                <For each={orgs()}>{(org) => (
                  <tr class="hover:bg-gray-900/50 transition-colors cursor-pointer" onClick={() => selectOrg(org.id)}>
                    <td class="px-4 py-3 text-gray-200 font-medium">{org.name}</td>
                    <td class="px-4 py-3 text-cyan-300 text-xs font-mono">{org.slug}</td>
                    <td class="px-4 py-3 text-gray-300 text-xs">{org.ownerEmail ?? "-"}</td>
                    <td class="px-4 py-3 text-gray-300 text-center">{org.memberCount}</td>
                    <td class="px-4 py-3 text-gray-500 text-xs">{org.createdAt?.slice(0, 10)}</td>
                  </tr>
                )}</For>
              </tbody>
            </table>
          </div>
        </Match>

        {/* ORG DETAIL */}
        <Match when={!!selectedOrgId()}>
          <div class="space-y-4">
            <div class="flex items-center justify-between gap-4">
              <div class="flex items-center gap-4">
                <button onClick={goBack} class="text-gray-400 hover:text-gray-200 cursor-pointer text-sm">← Back</button>
                <div>
                  <h2 class="text-lg font-semibold">{selOrg()?.name}</h2>
                  <div class="text-xs text-cyan-300 font-mono">Prefix slug: {selOrg()?.slug}</div>
                </div>
              </div>
              <button onClick={startEditOrg} class="px-3 py-1.5 bg-gray-800 text-gray-200 rounded text-sm hover:bg-gray-700 cursor-pointer">Edit Organization</button>
            </div>

            {/* Sub-tabs */}
            <div class="overflow-x-auto bg-gray-900 rounded-lg p-1">
              <div class="flex gap-1 whitespace-nowrap">
                {(["members", "auth", "users", "links", "invites", "projects", "cache"] as const).map((t) => (
                  <button onClick={() => switchOrgTab(t)} class={`px-3 py-1.5 text-sm rounded transition-colors cursor-pointer shrink-0 ${orgSubTab() === t ? "bg-gray-700 text-white" : "text-gray-400 hover:text-gray-200"}`}>
                    {t === "members" ? "Members" : t === "auth" ? "Auth" : t === "users" ? "Users" : t === "links" ? "Links" : t === "invites" ? "Invites" : t === "projects" ? "Projects" : "Cache"}
                  </button>
                ))}
              </div>
            </div>

            <Show when={loadingTab() !== orgSubTab()} fallback={<div class="text-gray-500 text-sm py-4">Loading...</div>}>
              <Switch>
                {/* MEMBERS */}
                <Match when={orgSubTab() === "members"}>
                  <div class="space-y-3">
                    <div class="flex items-center justify-between">
                      <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Members ({membersTotal()})</h3>
                      <button onClick={() => setShowAddMember(true)} class="text-xs px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Add Member</button>
                    </div>
                    <div class="border border-gray-800 rounded-lg overflow-x-auto">
                      <table class="w-full text-sm">
                        <thead class="bg-gray-900 text-gray-400"><tr><th class="text-left px-4 py-3 font-medium">Primary Email</th><th class="text-left px-4 py-3 font-medium">Display Name</th><th class="text-left px-4 py-3 font-medium">Email Verification</th><th class="text-left px-4 py-3 font-medium">Role</th><th class="text-left px-4 py-3 font-medium">Status</th><th class="text-right px-4 py-3 font-medium">Actions</th></tr></thead>
                        <tbody class="divide-y divide-gray-800">
                          <For each={members()}>{(m) => (
                            <tr class="hover:bg-gray-900/50">
                              <td class="px-4 py-3 text-gray-200">{m.userEmail}</td>
                              <td class="px-4 py-3 text-gray-300">{m.userDisplayName || "—"}</td>
                              <td class="px-4 py-3 text-xs">
                                <Show
                                  when={orgUsers().find((u: any) => u.id === m.userId)?.emailVerified}
                                  fallback={<span class="rounded border border-amber-700 bg-amber-900/40 px-2 py-0.5 text-amber-300">Unverified</span>}
                                >
                                  <span class="rounded border border-emerald-700 bg-emerald-900/40 px-2 py-0.5 text-emerald-300">Verified</span>
                                </Show>
                              </td>
                              <td class="px-4 py-3"><span class={`px-2 py-0.5 text-xs rounded border ${m.role === "owner" ? "bg-purple-900/40 text-purple-300 border-purple-700" : "bg-blue-900/40 text-blue-300 border-blue-700"}`}>{m.role}</span></td>
                              <td class="px-4 py-3"><span class={`px-2 py-0.5 text-xs rounded border ${m.status === "approved" ? "bg-emerald-900/40 text-emerald-300 border-emerald-700" : "bg-amber-900/40 text-amber-300 border-amber-700"}`}>{m.status}</span></td>
                              <td class="px-4 py-3 text-right">
                                <DropdownMenu label="Actions">
                                  <div class="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Role</div>
                                  <select value={m.role} onChange={(e) => doUpdateMember(m.userId, { role: e.currentTarget.value })} class="mx-2 mb-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200 cursor-pointer w-[calc(100%-1rem)]"><option value="owner">Owner</option><option value="manager">Manager</option><option value="searcher">Searcher</option></select>
                                  <div class="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Status</div>
                                  <select value={m.status} onChange={(e) => doUpdateMember(m.userId, { status: e.currentTarget.value })} class="mx-2 mb-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200 cursor-pointer w-[calc(100%-1rem)]"><option value="approved">Approved</option><option value="pending">Pending</option><option value="suspended">Suspended</option></select>
                                  <Show when={m.role !== "owner"}><button onClick={() => doRemoveMember(m.userId)} class="w-full text-left px-3 py-2 text-xs text-red-300 hover:bg-gray-700 cursor-pointer border-t border-gray-700">Remove Member</button></Show>
                                </DropdownMenu>
                              </td>
                            </tr>
                          )}</For>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <Pagination
                    page={membersPage()}
                    totalPages={Math.ceil(membersTotal() / membersPerPage())}
                    perPage={membersPerPage()}
                    total={membersTotal()}
                    loading={loadingTab() === "members"}
                    onPageChange={membersPageChange}
                    onPerPageChange={membersPerPageChange}
                  />
                </Match>

                {/* AUTH */}
                <Match when={orgSubTab() === "auth"}>
                  <div class="space-y-4">
                    <button
                      type="button"
                      onClick={() => setShowProviders((v) => !v)}
                      class="flex w-full items-center justify-between gap-3 cursor-pointer"
                    >
                      <div class="flex items-center gap-2">
                        <h3 class="text-sm font-semibold text-gray-200">
                          <Show when={showProviders()} fallback={<>Auth Providers ▼</>}>Auth Providers ▲</Show>
                        </h3>
                      </div>
                      <span class="text-xs text-gray-500">{providers().length} provider{providers().length !== 1 ? "s" : ""}</span>
                    </button>
                    <Show when={showProviders()}>
                      <div class="rounded-lg border border-gray-800 bg-gray-900 p-4">
                        <div class="mb-2 flex items-center justify-between gap-3">
                          <h3 class="text-sm font-semibold text-gray-200">Google OAuth Callback</h3>
                          <span class="rounded border border-cyan-800 bg-cyan-950/40 px-2 py-0.5 text-[10px] font-medium text-cyan-300">
                            Redirect URI
                          </span>
                        </div>
                        <p class="mb-2 text-xs text-gray-400">
                          Use this callback when configuring the Google provider for this environment.
                        </p>
                        <code class="block overflow-x-auto rounded border border-gray-800 bg-black/30 px-3 py-2 text-xs text-emerald-300">
                          {`${getAdminRuntimeConfig().managedAppBaseUrl}/auth/google/callback`}
                        </code>
                      </div>
                      <For each={providers()}>{(p) => (
                        <div class="bg-gray-900 border border-gray-800 rounded-lg p-4">
                          <div class="flex items-center justify-between mb-3">
                            <div class="flex items-center gap-3">
                              <span class={`w-2.5 h-2.5 rounded-full ${p.enabled ? "bg-emerald-400" : "bg-gray-600"}`} />
                              <span class="text-sm font-medium text-gray-200">{p.displayName}</span>
                            </div>
                            <button onClick={() => doToggleProvider(p.providerId, p.enabled ? 0 : 1)} class={`text-xs px-3 py-1 rounded border cursor-pointer ${p.enabled ? "bg-red-900/30 border-red-700 text-red-300 hover:bg-red-900/50" : "bg-emerald-900/30 border-emerald-700 text-emerald-300 hover:bg-emerald-900/50"}`}>
                              {p.enabled ? "Disable" : "Enable"}
                            </button>
                          </div>
                          <Show when={(PROVIDER_BASE_KEYS[p.providerName] ?? []).length > 0}>
                            <div class="text-xs text-gray-500 mb-2">Configuration overrides:</div>
                            <div class="space-y-2">
                              <For each={PROVIDER_BASE_KEYS[p.providerName] ?? []}>{(keyDef) => {
                                const val = () => (p.overrides ?? []).find((o) => o.key === keyDef.key)?.value ?? "";
                                const [editing, setEditing] = createSignal(false);
                                const [editVal, setEditVal] = createSignal("");
                                return (
                                  <div class="flex items-center gap-2 text-xs">
                                    <span class="text-gray-400 w-48">{keyDef.label}:</span>
                                    <span class="text-gray-300 font-mono">{val() || "(default)"}</span>
                                    <button onClick={() => { setEditing(true); setEditVal(val()); }} class="text-gray-500 hover:text-cyan-400 cursor-pointer">Edit</button>
                                    <Show when={editing()}>
                                      <input value={editVal()} onInput={(e) => setEditVal(e.currentTarget.value)} class="bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-gray-200 w-40" />
                                      <button onClick={async () => {
                                        try {
                                          await api.db.mutate(`INSERT OR REPLACE INTO organization_auth_config_overrides (id, organization_auth_provider_id, key, value) VALUES ('${crypto.randomUUID()}', '${p.id}', '${keyDef.key}', '${editVal().replace(/'/g, "''")}')`);
                                          setEditing(false); loadOrgTab(selectedOrgId()!, "auth");
                                        } catch (e: any) { setError(e.message); }
                                      }} class="text-emerald-400 hover:text-emerald-300 cursor-pointer">Save</button>
                                      <button onClick={() => setEditing(false)} class="text-gray-500 hover:text-gray-300 cursor-pointer">✕</button>
                </Show>
              </div>
                                );
                              }}</For>
                            </div>
                          </Show>
                        </div>
                      )}</For>
                    </Show>

                    <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide mt-6">Auth Sessions</h3>
                    <AuthSessionsTable organizationId={selectedOrgId()!} />

                    <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide mt-6">One-Time Login Links ({linksTotal()})</h3>
                    <Show when={links().length > 0} fallback={<div class="text-gray-600 text-sm bg-gray-900 border border-gray-800 rounded-lg p-6 text-center">No active login links</div>}>
                      <div class="border border-gray-800 rounded-lg overflow-x-auto">
                        <table class="w-full text-sm">
                          <thead class="bg-gray-900 text-gray-400 sticky top-0">
                            <tr>
                              <th class="text-left px-4 py-3 font-medium">User</th>
                              <th class="text-left px-4 py-3 font-medium">Created</th>
                              <th class="text-left px-4 py-3 font-medium">Expires</th>
                              <th class="text-left px-4 py-3 font-medium">Status</th>
                              <th class="text-right px-4 py-3 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody class="divide-y divide-gray-800">
                            <For each={
                              links().slice(
                                (authLinksPage() - 1) * AUTH_LINKS_PER_PAGE,
                                authLinksPage() * AUTH_LINKS_PER_PAGE,
                              )
                            }>
                              {(s) => (
                                <tr class="hover:bg-gray-900/50">
                                  <td class="px-4 py-3 text-gray-200">{s.userEmail}</td>
                                  <td class="px-4 py-3 text-gray-500 text-xs">{new Date(s.createdAt).toLocaleString()}</td>
                                  <td class="px-4 py-3 text-gray-500 text-xs">{new Date(s.expiresAt).toLocaleString()}</td>
                                  <td class="px-4 py-3">
                                    {s.usedAt ? (
                                      <span class="text-xs text-gray-500">Used {new Date(s.usedAt).toLocaleString()}</span>
                                    ) : new Date(s.expiresAt) < new Date() ? (
                                      <span class="text-xs text-red-400">Expired</span>
                                    ) : (
                                      <span class="text-xs text-emerald-400 flex items-center gap-1"><span class="w-1.5 h-1.5 rounded-full bg-emerald-400" />Active</span>
                                    )}
                                  </td>
                                  <td class="px-4 py-3 text-right">
                                    {s.usedAt || s.revokedAt
                                      ? <span class="text-xs text-gray-600">—</span>
                                      : <button onClick={() => { revokeLoginLink(s.id); }} class="text-xs px-2 py-1 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer">Revoke</button>
                                    }
                                  </td>
                                </tr>
                              )}
                            </For>
                          </tbody>
                        </table>
                      </div>
                      <Pagination
                        page={authLinksPage()}
                        totalPages={Math.ceil(linksTotal() / AUTH_LINKS_PER_PAGE)}
                        perPage={AUTH_LINKS_PER_PAGE}
                        total={linksTotal()}
                        loading={false}
                        onPageChange={setAuthLinksPage}
                        onPerPageChange={() => {}}
                      />
                    </Show>

                    <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide mt-6">Refresh Tokens</h3>
                    <RefreshTokensTable organizationId={selectedOrgId()!} />
                  </div>
                </Match>

                {/* USERS */}
                <Match when={orgSubTab() === "users"}>
                  <UsersTab
                    users={filteredUsers() || []}
                    members={members() || []}
                    orgId={selectedOrgId() ?? ""}
                    roleFilter={userRoleFilter()}
                    authFilter={authFilter()}
                    onRoleFilterChange={(r) => { setUserRoleFilter(r); loadOrgUsers(selectedOrgId()!); }}
                    onAuthFilterChange={setAuthFilter}
                    onCreateUser={() => setShowAddUser(true)}
                    onAddMember={() => setShowAddMember(true)}
                    onSaveEdit={handleSaveEdit}
                    onRemoveMember={doRemoveMember}
                    onLoginLink={(userId, email) => { setLinkGenUserId(userId); setLinkGenPresetEmail(email); setLinkGenResult(null); setLinkGenLockedUser(true); setShowLinkGen(true); }}
                    onSetPw={(userId, email) => { setSetPwUserId(userId); setSetPwEmail(email); setShowSetPw(true); }}
                    onVerifyEmail={doGenVerifyLink}
                    onVerifyEmailDirect={doVerifyEmailDirect}
                    onUnverifyEmail={doUnverifyEmail}
                  />
                  <Pagination
                    page={usersPage()}
                    totalPages={Math.ceil(usersTotal() / usersPerPage())}
                    perPage={usersPerPage()}
                    total={usersTotal()}
                    loading={loadingTab() === "users"}
                    onPageChange={usersPageChange}
                    onPerPageChange={usersPerPageChange}
                  />
                </Match>

                {/* LINKS */}
                <Match when={orgSubTab() === "links"}>
                  <div class="space-y-3">
                    <div class="flex items-center justify-between">
                      <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Login Links ({linksTotal()})</h3>
                      <button onClick={() => { setLinkGenUserId(""); setLinkGenPresetEmail(null); setLinkGenResult(null); setLinkGenLockedUser(false); setShowLinkGen(true); }} class="text-xs px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Generate Link</button>
                    </div>
                    <div class="border border-gray-800 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
                      <table class="w-full text-sm">
                        <thead class="bg-gray-900 text-gray-400 sticky top-0"><tr><th class="text-left px-4 py-3 font-medium">User</th><th class="text-left px-4 py-3 font-medium">Role</th><th class="text-left px-4 py-3 font-medium">Created</th><th class="text-left px-4 py-3 font-medium">Expires</th><th class="text-left px-4 py-3 font-medium">Status</th><th class="text-right px-4 py-3 font-medium">Actions</th></tr></thead>
                        <tbody class="divide-y divide-gray-800">
                          <For each={links()}>{(s) => (
                            <tr class="hover:bg-gray-900/50">
                              <td class="px-4 py-3 text-gray-200">{s.userEmail}</td>
                              <td class="px-4 py-3">{s.userRole ? <RoleBadge role={s.userRole} /> : <span class="text-gray-600 text-xs">—</span>}</td>
                              <td class="px-4 py-3 text-gray-500 text-xs">{new Date(s.createdAt).toLocaleString()}</td>
                              <td class="px-4 py-3 text-gray-500 text-xs">{new Date(s.expiresAt).toLocaleString()}</td>
                              <td class="px-4 py-3">{s.revokedAt ? <span class="text-xs text-red-400">Revoked {new Date(s.revokedAt).toLocaleString()}</span> : s.usedAt ? <span class="text-xs text-gray-500">Used {new Date(s.usedAt).toLocaleString()}</span> : <span class="text-xs text-emerald-400">Active</span>}</td>
                              <td class="px-4 py-3 text-right space-x-1"><Show when={!s.usedAt && !s.revokedAt}><button onClick={() => doRevokeLink(s.id)} class="text-xs px-2 py-0.5 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer">Revoke</button></Show><button onClick={() => { navigator.clipboard.writeText(managedOrgLoginUrl(s.orgSlug ?? "", s.token)); }} class="text-xs px-2 py-0.5 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 cursor-pointer">Copy</button></td>
                            </tr>
                          )}</For>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <Pagination
                    page={linksPage()}
                    totalPages={Math.ceil(linksTotal() / linksPerPage())}
                    perPage={linksPerPage()}
                    total={linksTotal()}
                    loading={loadingTab() === "links"}
                    onPageChange={linksPageChange}
                    onPerPageChange={linksPerPageChange}
                  />
                  <VerificationLinksSection
                    organizationId={selectedOrgId()!}
                    onGenerate={() => {
                      setVerifyLinkGenUserId("");
                      setVerifyLinkGenEmail("");
                      setVerifyLinkResult(null);
                      setShowVerifyLinkGen(true);
                    }}
                  />
                </Match>

                {/* INVITES */}
                <Match when={orgSubTab() === "invites"}>
                  <div class="space-y-3">
                    <div class="flex items-center justify-between">
                      <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Invites ({invitesTotal()})</h3>
                      <button onClick={() => setShowInvite(true)} class="text-xs px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Create Invite</button>
                    </div>
                    <div class="border border-gray-800 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
                      <table class="w-full text-sm">
                        <thead class="bg-gray-900 text-gray-400 sticky top-0"><tr><th class="text-left px-4 py-3 font-medium">Email</th><th class="text-left px-4 py-3 font-medium">Role</th><th class="text-left px-4 py-3 font-medium">Created</th><th class="text-left px-4 py-3 font-medium">Status</th><th class="text-right px-4 py-3 font-medium">Actions</th></tr></thead>
                        <tbody class="divide-y divide-gray-800">
                          <For each={invites()}>{(inv) => (
                            <tr class="hover:bg-gray-900/50">
                              <td class="px-4 py-3 text-gray-200">{inv.email}</td>
                              <td class="px-4 py-3 text-gray-400 text-xs">{inv.role}</td>
                              <td class="px-4 py-3 text-gray-500 text-xs">{new Date(inv.createdAt).toLocaleString()}</td>
                              <td class="px-4 py-3">{inv.consumedAt ? <span class="text-xs text-gray-500">Used</span> : <span class="text-xs text-emerald-400">Active</span>}</td>
                              <td class="px-4 py-3 text-right"><Show when={!inv.consumedAt}><button onClick={() => doRemoveInvite(inv.id)} class="text-xs px-2 py-0.5 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer">Revoke</button></Show></td>
                            </tr>
                          )}</For>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <Pagination
                    page={invitesPage()}
                    totalPages={Math.ceil(invitesTotal() / invitesPerPage())}
                    perPage={invitesPerPage()}
                    total={invitesTotal()}
                    loading={loadingTab() === "invites"}
                    onPageChange={invitesPageChange}
                    onPerPageChange={invitesPerPageChange}
                  />
                </Match>

                <Match when={orgSubTab() === "cache"}>
                  <OrganizationCacheTab
                    organizationId={selectedOrgId()!}
                    projects={projects()}
                  />
                </Match>

                {/* PROJECTS */}
                <Match when={orgSubTab() === "projects"}>
                  <div class="space-y-3">
                    <div class="flex items-center justify-between">
                      <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Projects ({projects().length})</h3>
                      <div class="flex items-center gap-2">
                        <button onClick={() => void invalidateOrganizationProjectBlockCaches()} class="text-xs px-3 py-1 bg-amber-900/30 border border-amber-700 text-amber-300 rounded hover:bg-amber-900/50 cursor-pointer">Invalidate Org Caches</button>
                        <button onClick={() => setShowCreateProject(true)} class="text-xs px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Create Project</button>
                      </div>
                    </div>
                    <div class="border border-gray-800 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
                      <table class="w-full text-sm">
                        <thead class="bg-gray-900 text-gray-400 sticky top-0"><tr><th class="text-left px-4 py-3 font-medium w-6"></th><th class="text-left px-4 py-3 font-medium">Name</th><th class="text-left px-4 py-3 font-medium">Lifecycle</th><th class="text-left px-4 py-3 font-medium">Visibility</th><th class="text-left px-4 py-3 font-medium">Cache</th><th class="text-center px-4 py-3 font-medium">Members</th><th class="text-right px-4 py-3 font-medium">Actions</th></tr></thead>
                        <tbody class="divide-y divide-gray-800">
                          <For each={projects()}>{(p) => (
                            <>
                            <tr class="hover:bg-gray-900/50 cursor-pointer" onClick={() => toggleProjectMembers(p.id)}>
                              <td class="px-4 py-3 text-gray-600">{expandedProject() === p.id ? "▼" : "▶"}</td>
                              <td class="px-4 py-3 text-gray-200">
                                <div>{p.name}</div>
                                <div class="text-[11px] text-gray-500 font-mono">{p.slug}{p.fullSlug ? ` (${p.fullSlug})` : ""}</div>
                              </td>
                              <td class="px-4 py-3 text-gray-400 text-xs">{p.lifecycle}</td>
                              <td class="px-4 py-3 text-gray-400 text-xs">{p.visibility}</td>
                              <td class="px-4 py-3 text-xs">
                                <div class="text-[10px] uppercase tracking-wide text-gray-500">project_blocks</div>
                                <div class="text-gray-300 capitalize">{projectCacheStatus(p)}</div>
                                <div class="text-[11px] text-gray-500">{p.blocksCacheGeneratedAt ? p.blocksCacheGeneratedAt.slice(0, 19).replace("T", " ") : "Not built"}</div>
                              </td>
                              <td class="px-4 py-3 text-gray-400 text-xs text-center">{p.memberCount}</td>
                              <td class="px-4 py-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                                <div class="flex gap-1.5">
                                  <button onClick={() => startEditProject(p)} class="flex-1 text-xs px-2 py-1 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 cursor-pointer text-center">Edit</button>
                                  <button onClick={() => void invalidateProjectBlockCache(p.id)} class="flex-1 text-xs px-2 py-1 bg-amber-900/30 border border-amber-700 text-amber-300 rounded hover:bg-amber-900/50 cursor-pointer text-center whitespace-nowrap">Invalidate Cache</button>
                                </div>
                                <div class="flex gap-1.5">
                                  <a href={managedProjectUrl(p.fullSlug ?? p.id)} target="_blank" rel="noreferrer" class="flex-1 text-xs px-2 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer text-center">Project</a>
                                  <CopyButton link={managedProjectUrl(p.fullSlug ?? p.id)} iconOnly />
                                </div>
                                <div class="flex gap-1.5">
                                  <a href={managedSearcherUrl(p.fullSlug ?? p.id)} target="_blank" rel="noreferrer" class="flex-1 text-xs px-2 py-1 bg-emerald-900/30 border border-emerald-700 text-emerald-300 rounded hover:bg-emerald-900/50 cursor-pointer text-center">Search</a>
                                  <CopyButton link={managedSearcherUrl(p.fullSlug ?? p.id)} iconOnly />
                                </div>
                              </td>
                            </tr>
                            <Show when={expandedProject() === p.id}>
                              <tr>
                                <td colspan="7" class="bg-gray-950 p-0">
                                  <div class="px-4 py-3 border-t border-gray-800">
                                    <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Members</h4>
                                    <div class="flex items-center justify-between mb-2">
                                      <span class="text-[10px] text-gray-500">{projectMembers()[p.id]?.length ?? 0} member{projectMembers()[p.id]?.length !== 1 ? "s" : ""}</span>
                                      <button onClick={() => { setAddProjectMemberProjectId(p.id); setShowAddProjectMember(true); }} class="text-xs px-2 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Add Member</button>
                                    </div>
                                    <Show when={!loadingProjectMembers()} fallback={<div class="text-gray-500 text-xs py-2">Loading...</div>}>
                                      <Show when={projectMembers()[p.id]} fallback={<div class="text-gray-600 text-xs py-2">No members or failed to load</div>}>
                                        <table class="w-full text-xs">
                                          <thead class="text-gray-500"><tr><th class="text-left py-1 font-medium">User</th><th class="text-left py-1 font-medium">Role</th><th class="text-left py-1 font-medium">Status</th><th class="text-right py-1 font-medium">Actions</th></tr></thead>
                                          <tbody class="divide-y divide-gray-800/50">
                                            <For each={projectMembers()[p.id]}>
                                              {(m) => (
                                                <tr>
                                                  <td class="py-1.5 text-gray-200">{m.userEmail}</td>
                                                  <td class="py-1.5">
                                                    {m.role === "owner" ? (
                                                      <span class="px-2 py-0.5 text-xs font-medium rounded border bg-purple-900/40 text-purple-300 border-purple-700">{m.role}</span>
                                                    ) : (
                                                      <select
                                                        value={m.role}
                                                        onChange={(e) => updateProjectMemberRole(p.id, m.userId, e.currentTarget.value)}
                                                        class="bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-xs text-gray-200 cursor-pointer"
                                                      >
                                                        <option value="manager">manager</option>
                                                        <option value="searcher">searcher</option>
                                                      </select>
                                                    )}
                                                  </td>
                                                  <td class="py-1.5"><span class={`px-2 py-0.5 text-xs font-medium rounded border ${m.status === "approved" ? "bg-emerald-900/40 text-emerald-300 border-emerald-700" : "bg-amber-900/40 text-amber-300 border-amber-700"}`}>{m.status}</span></td>
                                                  <td class="py-1.5 text-right">
                                                    {m.role !== "owner" && (
                                                      <button onClick={() => removeProjectMember(p.id, m.userId)} class="text-xs px-2 py-0.5 text-red-400 hover:text-red-300 cursor-pointer">Remove</button>
                                                    )}
                                                  </td>
                                                </tr>
                                              )}
                                            </For>
                                          </tbody>
                                        </table>
                                      </Show>
                                    </Show>
                                  </div>
                                </td>
                              </tr>
                            </Show>
                            </>
                          )}</For>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Match>
              </Switch>
            </Show>
          </div>
        </Match>
      </Switch>

      {/* Create Org Modal */}
      <Show when={showCreate()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowCreate(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Create Organization</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Name</label><input value={newOrgName()} onInput={(e) => setNewOrgName(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Prefix Slug</label>
                <input value={newOrgSlug()} onInput={(e) => setNewOrgSlug(normalizeSegment(e.currentTarget.value))} placeholder="cf" class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600 font-mono" />
                <div class="mt-1 text-xs text-gray-500">Use lowercase letters and numbers only.</div>
              </div>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={createOrgWithUser()} onChange={(e) => setCreateOrgWithUser(e.currentTarget.checked)} class="rounded bg-gray-800 border-gray-700 text-cyan-600" />
                <span class="text-sm text-gray-400">Create new owner user</span>
              </label>
              <Show when={createOrgWithUser()} fallback={
                <div><label class="block text-sm text-gray-400 mb-1">Owner</label><select value={newOrgOwner()} onChange={(e) => setNewOrgOwner(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="">Select user...</option><For each={users()}>{(u) => <option value={u.id}>{u.email}</option>}</For></select></div>
              }>
                <div class="space-y-3">
                  <div><label class="block text-sm text-gray-400 mb-1">Email</label><input value={createOrgUserEmail()} onInput={(e) => setCreateOrgUserEmail(e.currentTarget.value)} type="email" class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
                  <div><label class="block text-sm text-gray-400 mb-1">Display Name</label><input value={createOrgUserDisplayName()} onInput={(e) => setCreateOrgUserDisplayName(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
                  <div><label class="block text-sm text-gray-400 mb-1">Password</label><input type="password" value={createOrgUserPassword()} onInput={(e) => setCreateOrgUserPassword(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
                </div>
              </Show>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowCreate(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doCreateOrg} disabled={!newOrgName() || !newOrgSlug() || (!createOrgWithUser() && !newOrgOwner()) || (createOrgWithUser() && (!createOrgUserEmail() || !createOrgUserDisplayName() || !createOrgUserPassword()))} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">Create</button></div>
          </div>
        </div>
      </Show>

      <Show when={showEditOrg()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowEditOrg(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Edit Organization</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Name</label><input value={editOrgName()} onInput={(e) => setEditOrgName(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Prefix Slug</label><input value={editOrgSlug()} onInput={(e) => setEditOrgSlug(normalizeSegment(e.currentTarget.value))} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600 font-mono" /></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowEditOrg(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doEditOrg} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 cursor-pointer">Save</button></div>
          </div>
        </div>
      </Show>

      {/* Add Member Modal */}
      <Show when={showAddMember()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowAddMember(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Add Member</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">User</label><select value={addMemberUserId()} onChange={(e) => setAddMemberUserId(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="">Select user...</option><For each={users()}>{(u) => <option value={u.id}>{u.email}</option>}</For></select></div>
              <div><label class="block text-sm text-gray-400 mb-1">Role</label><select value={addMemberRole()} onChange={(e) => setAddMemberRole(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="owner">Owner</option><option value="manager">Manager</option><option value="searcher">Searcher</option></select></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowAddMember(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doAddMember} disabled={!addMemberUserId()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">Add</button></div>
          </div>
        </div>
      </Show>

      {/* Generate Link Modal */}
      <Show when={showLinkGen()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={closeLinkGenModal} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Generate Login Link</h3>
            <div class="space-y-4">
              <Show
                when={linkGenLockedUser()}
                fallback={<div><label class="block text-sm text-gray-400 mb-1">User</label><select value={linkGenUserId()} onChange={(e) => setLinkGenUserId(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="">Select user...</option><For each={orgUsers()}>{(u: any) => <option value={u.id}>{u.email}</option>}</For></select></div>}
              >
                <div>
                  <label class="block text-sm text-gray-400 mb-1">Selected User</label>
                  <div class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200">
                    {linkGenPresetEmail() ?? ""}
                  </div>
                </div>
              </Show>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Link Expiry</label>
                <select value={linkGenExpiry()} onChange={(e) => setLinkGenExpiry(Number(e.currentTarget.value))} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer">
                  <option value={300}>5 minutes</option>
                  <option value={900}>15 minutes</option>
                  <option value={3600}>1 hour</option>
                  <option value={14400}>4 hours</option>
                  <option value={86400}>24 hours</option>
                  <option value={172800}>2 days</option>
                  <option value={259200}>3 days</option>
                  <option value={604800}>7 days</option>
                </select>
              </div>
            </div>
            <Show when={linkGenResult()}>
              <div class="mb-3">
                <LinkPreview href={linkGenResult()!.link} />
              </div>
              <div class="flex justify-end gap-3 mb-3">
                <CopyButton link={linkGenResult()!.link} compact />
              </div>
            </Show>
            <div class="flex justify-end gap-3 pt-2"><button onClick={closeLinkGenModal} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={async () => { await doGenLoginLink(linkGenUserId()); }} disabled={!linkGenUserId()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">{linkGenResult() ? "Regenerate" : "Generate"}</button></div>
          </div>
        </div>
      </Show>

      {/* Generate Email Verification Link Modal */}
      <Show when={showVerifyLinkGen()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={closeVerifyLinkGenModal} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Generate Verification Link</h3>
            <div class="space-y-4">
              <Show
                when={verifyLinkGenUserId()}
                fallback={<div><label class="block text-sm text-gray-400 mb-1">User</label><select value={verifyLinkGenUserId()} onChange={(e) => setVerifyLinkGenUserId(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="">Select user...</option><For each={orgUsers()}>{(u: any) => <option value={u.id}>{u.email}</option>}</For></select></div>}
              >
                <div>
                  <label class="block text-sm text-gray-400 mb-1">Selected User</label>
                  <div class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200">
                    {verifyLinkGenEmail() ?? ""}
                  </div>
                </div>
              </Show>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Link Expiry</label>
                <select value={verifyLinkGenExpiry()} onChange={(e) => setVerifyLinkGenExpiry(Number(e.currentTarget.value))} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer">
                  <option value={1}>1 hour</option>
                  <option value={6}>6 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                  <option value={48}>48 hours</option>
                  <option value={72}>3 days</option>
                  <option value={168}>7 days</option>
                </select>
              </div>
            </div>
            <Show when={verifyLinkResult()}>
              <div class="mb-3">
                <LinkPreview href={verifyLinkResult()!.link} />
              </div>
              <div class="flex justify-end gap-3 mb-3">
                <CopyButton link={verifyLinkResult()!.link} compact />
              </div>
            </Show>
            <div class="flex justify-end gap-3 pt-2">
              <button onClick={closeVerifyLinkGenModal} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button>
              <button
                onClick={async () => {
                  if (verifyLinkGenUserId()) {
                    await doGenVerifyLink(verifyLinkGenUserId(), verifyLinkGenEmail());
                  }
                }}
                disabled={!verifyLinkGenUserId() && !verifyLinkGenEmail()}
                class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">
                {verifyLinkResult() ? "Regenerate" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Set Password Modal */}
      <Show when={showSetPw()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowSetPw(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Set Password</h3>
            <p class="text-sm text-gray-400 mb-3">{setPwEmail()}</p>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">New Password</label><input type="password" value={setPwPassword()} onInput={(e) => setSetPwPassword(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowSetPw(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={async () => { try { await api.users.resetPassword(setPwUserId(), setPwPassword()); setShowSetPw(false); setSetPwPassword(""); } catch (e: any) { setError(e.message); } }} disabled={!setPwPassword()} class="px-4 py-2 bg-amber-600 text-white rounded text-sm font-medium hover:bg-amber-500 disabled:opacity-50 cursor-pointer">Set Password</button></div>
          </div>
        </div>
      </Show>

      {/* Add User Modal */}
      <Show when={showAddUser()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowAddUser(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Create User</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Email</label><input type="email" value={addUserEmail()} onInput={(e) => setAddUserEmail(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Display Name</label><input value={addUserDisplayName()} onInput={(e) => setAddUserDisplayName(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Password (optional)</label><input type="password" value={addUserPassword()} onInput={(e) => setAddUserPassword(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Role</label><select value={addUserRole()} onChange={(e) => setAddUserRole(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="owner">Owner</option><option value="manager">Manager</option><option value="searcher">Searcher</option></select></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowAddUser(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doAddUser} disabled={!addUserEmail()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">Create</button></div>
          </div>
        </div>
      </Show>

      {/* Create Invite Modal */}
      <Show when={showInvite()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowInvite(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Create Invite</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Email</label><input type="email" value={inviteEmail()} onInput={(e) => setInviteEmail(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Role</label><select value={inviteRole()} onChange={(e) => setInviteRole(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="manager">Manager</option><option value="searcher">Searcher</option></select></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowInvite(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doCreateInvite} disabled={!inviteEmail()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">Create</button></div>
          </div>
        </div>
      </Show>

      {/* Create Project Modal */}
      <Show when={showCreateProject()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowCreateProject(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Create Project</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Name</label><input value={cpName()} onInput={(e) => setCpName(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Description</label><input value={cpDescription()} onInput={(e) => setCpDescription(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Slug</label>
                <input value={cpSlug()} onInput={(e) => setCpSlug(normalizeSegment(e.currentTarget.value))} placeholder="my-project" class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600 font-mono" />
                <div class="mt-1 text-xs text-gray-500">Full slug: <span class="text-cyan-300 font-mono">{createProjectFullSlug()}</span></div>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="block text-sm text-gray-400 mb-1">Visibility</label><select value={cpVisibility()} onChange={(e) => setCpVisibility(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="private">Private</option><option value="unlisted">Unlisted</option><option value="public">Public</option></select></div>
                <div><label class="block text-sm text-gray-400 mb-1">Access Mode</label><select value={cpAccessMode()} onChange={(e) => setCpAccessMode(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="open">Open</option><option value="restricted">Restricted</option></select></div>
              </div>
              <div><label class="block text-sm text-gray-400 mb-1">Block Size (m)</label><input type="number" value={cpBlockSize()} onInput={(e) => setCpBlockSize(Number(e.currentTarget.value))} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Owner</label><select value={cpOwnerId()} onChange={(e) => setCpOwnerId(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="">Select user...</option><For each={orgUsers()}>{(u: any) => <option value={u.id}>{u.email}</option>}</For></select></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowCreateProject(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doCreateProjectInOrg} disabled={!cpName() || cpCreating()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">{cpCreating() ? "Creating..." : "Create"}</button></div>
          </div>
        </div>
      </Show>

      {/* Edit Project Modal */}
      <Show when={showEditProject()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowEditProject(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Edit Project</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Name</label><input value={epName()} onInput={(e) => setEpName(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Description</label><input value={epDescription()} onInput={(e) => setEpDescription(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Slug</label>
                <input value={epSlug()} onInput={(e) => setEpSlug(normalizeSegment(e.currentTarget.value))} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600 font-mono" />
                <div class="mt-1 text-xs text-gray-500">Full slug: <span class="text-cyan-300 font-mono">{editProjectFullSlug()}</span></div>
              </div>
              <div><label class="block text-sm text-gray-400 mb-1">Lifecycle</label><select value={epLifecycle()} onChange={(e) => setEpLifecycle(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="draft">Draft</option><option value="active">Active</option><option value="published">Published</option><option value="paused">Paused</option><option value="closed">Closed</option></select></div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="block text-sm text-gray-400 mb-1">Visibility</label><select value={epVisibility()} onChange={(e) => setEpVisibility(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="private">Private</option><option value="unlisted">Unlisted</option><option value="public">Public</option></select></div>
                <div><label class="block text-sm text-gray-400 mb-1">Access Mode</label><select value={epAccessMode()} onChange={(e) => setEpAccessMode(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="open">Open</option><option value="restricted">Restricted</option></select></div>
              </div>
              <div><label class="block text-sm text-gray-400 mb-1">Block Size (m)</label><input type="number" value={epBlockSize()} onInput={(e) => setEpBlockSize(Number(e.currentTarget.value))} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowEditProject(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doEditProject} disabled={epSaving()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">{epSaving() ? "Saving..." : "Save"}</button></div>
          </div>
        </div>
      </Show>

      {/* Add Project Member Modal */}
      <Show when={showAddProjectMember()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowAddProjectMember(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Add Project Member</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">User</label><select value={addProjectMemberUserId()} onChange={(e) => setAddProjectMemberUserId(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="">Select user...</option><For each={users()}>{(u) => <option value={u.id}>{u.email}</option>}</For></select></div>
              <div><label class="block text-sm text-gray-400 mb-1">Role</label><select value={addProjectMemberRole()} onChange={(e) => setAddProjectMemberRole(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="manager">Manager</option><option value="searcher">Searcher</option></select></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowAddProjectMember(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={addProjectMember} disabled={!addProjectMemberUserId()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">Add</button></div>
          </div>
        </div>
      </Show>
    </div>
  );
}
